// Widget Store - manages widget configuration (pin/order stored in KV)
// Signals are managed by SignalStore

import { create } from 'zustand';
import { getKV, setKV } from '../api/kv';

export interface WidgetConfig {
  pinnedDatum: string[]; // Signal IDs that are pinned
  order: string[]; // Ordered list of signal IDs
}

interface WidgetState {
  // Widget configuration (KV-based)
  config: WidgetConfig;
  configLoading: boolean;

  // Actions
  loadConfig: (tenant: string) => Promise<void>;
  togglePin: (tenant: string, signalId: string) => Promise<void>;
  reorderDatum: (tenant: string, orderedDatumIds: string[]) => Promise<void>;

  // Helpers
  isPinned: (signalId: string) => boolean;

  reset: () => void;
}

export const useWidgetStore = create<WidgetState>((set, get) => ({
  config: {
    pinnedDatum: [],
    order: [],
  },
  configLoading: false,

  loadConfig: async (tenant) => {
    set({ configLoading: true });
    try {
      const response = await getKV(tenant, 'widgets');
      if (response?.value) {
        const config = response.value as unknown as WidgetConfig;
        set({
          config: {
            pinnedDatum: config.pinnedDatum || [],
            order: config.order || [],
          },
          configLoading: false,
        });
      } else {
        // Key not found - use default empty config
        set({
          config: { pinnedDatum: [], order: [] },
          configLoading: false,
        });
      }
    } catch (error) {
      console.error('Failed to load widget config:', error);
      // On error, use default empty config
      set({
        config: { pinnedDatum: [], order: [] },
        configLoading: false,
      });
    }
  },

  togglePin: async (tenant, signalId) => {
    const { config } = get();
    const isPinned = config.pinnedDatum.includes(signalId);

    const newPinnedDatum = isPinned
      ? config.pinnedDatum.filter((id) => id !== signalId)
      : [...config.pinnedDatum, signalId];

    try {
      await setKV(tenant, 'widgets', {
        pinnedDatum: newPinnedDatum,
        order: config.order,
      });

      set({
        config: {
          ...config,
          pinnedDatum: newPinnedDatum,
        },
      });
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      throw error;
    }
  },

  reorderDatum: async (tenant, orderedSignalIds) => {
    const { config } = get();

    try {
      await setKV(tenant, 'widgets', {
        pinnedSignals: config.pinnedDatum,
        order: orderedSignalIds,
      });

      set({
        config: {
          ...config,
          order: orderedSignalIds,
        },
      });
    } catch (error) {
      console.error('Failed to reorder signals:', error);
      throw error;
    }
  },

  isPinned: (signalId) => {
    return get().config.pinnedDatum.includes(signalId);
  },

  reset: () => {
    set({
      config: { pinnedDatum: [], order: [] },
      configLoading: false,
    });
  },
}));
